import React from 'react';
import './Pagination.css';

/**
 * Pagination — presentational page-control footer.
 *
 * A faithful extraction of the Usage page's "Recent executions" pager:
 * a left-aligned "X–Y of N" range readout and right-aligned
 * Prev · "Page P / T" · Next controls. The component owns the markup +
 * styling only; the page owns the data and slicing (see usePagination).
 *
 * Renders nothing when there's a single page (total <= pageSize), matching
 * the original behaviour where the footer only appeared once the row set
 * overflowed one page.
 *
 * Props:
 *   page         — current page (1-based)
 *   pageCount    — total number of pages
 *   total        — total row count (for the range readout)
 *   pageSize     — rows per page (for the range readout)
 *   onPageChange — (nextPage) => void
 *   className    — optional extra class on the footer wrapper
 */
const Pagination = ({ page, pageCount, total, pageSize, onPageChange, className = '' }) => {
  if (!pageCount || pageCount <= 1) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <div className={`pagination ${className}`.trim()}>
      <span className="pagination-info">
        {first}–{last} of {total}
      </span>
      <div className="pagination-controls">
        <button
          type="button"
          className="pagination-btn"
          disabled={page <= 1}
          onClick={() => onPageChange(Math.max(1, page - 1))}
        >
          Prev
        </button>
        <span className="pagination-page">Page {page} / {pageCount}</span>
        <button
          type="button"
          className="pagination-btn"
          disabled={page >= pageCount}
          onClick={() => onPageChange(Math.min(pageCount, page + 1))}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Pagination;
